import React from "react";
import { Text, View, StyleSheet, Svg, Path } from "@react-pdf/renderer";
import { TreatmentPlanAppointment } from "../../services/treatmentPlanService";
import { deduplicateCdtCodesStructured } from "../../utils/cdtCodeUtils";
import {
  formatFeeWithoutCents,
  getAppointmentFee,
  getOptionLabel,
  getProcedureFee,
} from "../../utils/appointmentDisplayUtils";
import { parseToothSurfaces } from "../../utils/toothSurfacesUtils";
import {
  PDF_APPOINTMENT_META_GAP,
  PDF_APPOINTMENT_META_WIDTH,
  PDF_APPOINTMENT_NAME_WIDTH,
  PDF_APPOINTMENT_NUMBER_WIDTH,
  PDF_ROW_PADDING,
  PDF_TABLE_CDT_WIDTH,
  PDF_TABLE_TOOTH_WIDTH,
  PDF_TABLE_SURFACE_WIDTH,
  PDF_TABLE_FEE_WIDTH,
  PDF_TABLE_GAP,
  PDF_TABLE_NAME_WIDTH,
  pdfColors,
} from "./treatmentPlanPdfConstants";

const colors = pdfColors;

const styles = StyleSheet.create({
  appointmentsTableHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: PDF_TABLE_GAP,
    paddingBottom: 6,
    paddingHorizontal: PDF_ROW_PADDING,
  },
  appointmentsTableHeaderName: {
    width: PDF_TABLE_NAME_WIDTH,
    fontFamily: "Nunito",
    fontSize: 7,
    fontWeight: 700,
    color: colors.secondary,
  },
  appointmentsTableHeaderCdt: {
    width: PDF_TABLE_CDT_WIDTH,
    fontFamily: "Nunito",
    fontSize: 7,
    fontWeight: 700,
    color: colors.secondary,
    flexShrink: 0,
  },
  appointmentsTableHeaderTooth: {
    width: PDF_TABLE_TOOTH_WIDTH,
    fontFamily: "Nunito",
    fontSize: 7,
    fontWeight: 700,
    color: colors.secondary,
    flexShrink: 0,
  },
  appointmentsTableHeaderSurface: {
    width: PDF_TABLE_SURFACE_WIDTH,
    fontFamily: "Nunito",
    fontSize: 7,
    fontWeight: 700,
    color: colors.secondary,
    flexShrink: 0,
  },
  appointmentsTableHeaderFee: {
    width: PDF_TABLE_FEE_WIDTH,
    fontFamily: "Nunito",
    fontSize: 7,
    fontWeight: 700,
    color: colors.secondary,
    textAlign: "right",
    flexShrink: 0,
  },
  appointmentContainer: {
    marginBottom: 8,
  },
  appointmentHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: PDF_APPOINTMENT_META_GAP,
    borderRadius: 5,
    paddingVertical: 5,
    paddingHorizontal: PDF_ROW_PADDING,
    backgroundColor: colors.mediumBlue,
    marginBottom: 0,
  },
  appointmentNameWrap: {
    width: PDF_APPOINTMENT_NAME_WIDTH,
    flexShrink: 0,
  },
  appointmentMetaGroup: {
    width: PDF_APPOINTMENT_META_WIDTH,
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    flexShrink: 0,
  },
  appointmentNumber: {
    width: PDF_APPOINTMENT_NUMBER_WIDTH,
    fontFamily: "Sora",
    fontSize: 8,
    fontWeight: 600,
    color: colors.action,
    flexShrink: 0,
  },
  appointmentName: {
    fontFamily: "Sora",
    fontSize: 8,
    fontWeight: 600,
    color: colors.link,
    lineHeight: 1.3,
  },
  optionBadge: {
    backgroundColor: colors.dark,
    color: colors.white,
    paddingVertical: 1,
    paddingHorizontal: 4,
    borderRadius: 3,
    fontSize: 7,
    fontWeight: 700,
    fontFamily: "Nunito",
    flexShrink: 0,
  },
  appointmentDuration: {
    fontSize: 7,
    fontWeight: 700,
    fontFamily: "Nunito",
    color: colors.primary,
    flexShrink: 0,
  },
  appointmentFee: {
    width: PDF_TABLE_FEE_WIDTH,
    fontSize: 7,
    fontWeight: 700,
    fontFamily: "Nunito",
    color: colors.text,
    textAlign: "right",
    flexShrink: 0,
  },
  procedureRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: PDF_TABLE_GAP,
    paddingVertical: 6,
    paddingHorizontal: PDF_ROW_PADDING,
    borderBottomWidth: 1,
    borderBottomColor: colors.background,
  },
  procedureTitle: {
    width: PDF_TABLE_NAME_WIDTH,
    fontFamily: "Nunito",
    fontSize: 7,
    fontWeight: 700,
    color: colors.link,
  },
  procedureCdtColumn: {
    width: PDF_TABLE_CDT_WIDTH,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    flexShrink: 0,
  },
  procedureToothColumn: {
    width: PDF_TABLE_TOOTH_WIDTH,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    flexShrink: 0,
  },
  procedureSurfaceColumn: {
    width: PDF_TABLE_SURFACE_WIDTH,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    flexShrink: 0,
  },
  cdtChip: {
    borderWidth: 1,
    borderColor: colors.action,
    backgroundColor: colors.lightBlue,
    borderRadius: 8,
    paddingVertical: 1,
    paddingHorizontal: 4,
  },
  cdtChipText: {
    fontFamily: "Nunito",
    fontSize: 6,
    fontWeight: 600,
    color: colors.action,
  },
  cdtMultiplier: {
    color: colors.secondary,
  },
  procedureEmptyValue: {
    fontFamily: "Nunito",
    fontSize: 6,
    fontWeight: 400,
    color: colors.border,
    textTransform: "uppercase",
  },
  procedureFee: {
    width: PDF_TABLE_FEE_WIDTH,
    fontFamily: "Nunito",
    fontSize: 7,
    fontWeight: 600,
    color: colors.text,
    textAlign: "right",
    flexShrink: 0,
  },
  appointmentTimeframeRow: {
    paddingTop: 6,
    paddingHorizontal: 8,
  },
  timeSeparator: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 5,
    paddingVertical: 4,
    paddingHorizontal: 4,
    backgroundColor: colors.primary,
    borderRadius: 4,
    maxWidth: "100%",
  },
  timeSeparatorLabel: {
    fontFamily: "Sora",
    fontSize: 6,
    fontWeight: 600,
    color: colors.white,
  },
});

const getAppointmentDurationMinutes = (
  appointment: TreatmentPlanAppointment,
): number => {
  if (appointment.totalDurationMinutes != null) {
    return appointment.totalDurationMinutes;
  }
  let total = 0;
  appointment.steps.forEach((step) => {
    total += step.durationMinutes ?? 20;
  });
  return total;
};

const formatCdtCodeEntries = (
  codeSuggestions: TreatmentPlanAppointment["steps"][0]["codeSuggestions"],
) => deduplicateCdtCodesStructured(codeSuggestions);

const CalendarIconWhite: React.FC = () => (
  <Svg width={8} height={8} viewBox="0 0 25 24">
    <Path
      d="M7.7998 11H9.7998V13H7.7998V11ZM7.7998 15H9.7998V17H7.7998V15ZM11.7998 11H13.7998V13H11.7998V11ZM11.7998 15H13.7998V17H11.7998V15ZM15.7998 11H17.7998V13H15.7998V11ZM15.7998 15H17.7998V17H15.7998V15Z"
      fill="#FFFFFF"
    />
    <Path
      d="M5.7998 22H19.7998C20.9028 22 21.7998 21.103 21.7998 20V6C21.7998 4.897 20.9028 4 19.7998 4H17.7998V2H15.7998V4H9.7998V2H7.7998V4H5.7998C4.6968 4 3.7998 4.897 3.7998 6V20C3.7998 21.103 4.6968 22 5.7998 22ZM19.7998 8L19.8008 20H5.7998V8H19.7998Z"
      fill="#FFFFFF"
    />
  </Svg>
);

const DownArrowIconWhite: React.FC = () => (
  <Svg
    width={8}
    height={8}
    viewBox="0 0 20 21"
    style={{ transform: "rotate(90deg)" }}
  >
    <Path
      d="M9.41083 14.9109L10.5892 16.0892L16.1783 10.5001L10.5892 4.91089L9.41083 6.08922L12.9883 9.66672H5V11.3334H12.9883L9.41083 14.9109Z"
      fill="#FFFFFF"
    />
  </Svg>
);

const AppointmentsTableHeader: React.FC = () => (
  <View style={styles.appointmentsTableHeader} wrap={false}>
    <Text style={styles.appointmentsTableHeaderName}>Appt/Step Name</Text>
    <Text style={styles.appointmentsTableHeaderCdt}>Code</Text>
    <Text style={styles.appointmentsTableHeaderTooth}>Tooth</Text>
    <Text style={styles.appointmentsTableHeaderSurface}>Surface</Text>
    <Text style={styles.appointmentsTableHeaderFee}>Fee</Text>
  </View>
);

interface ProcedureRowProps {
  step: TreatmentPlanAppointment["steps"][0];
}

const renderValueChips = (values: string[] | undefined, emptyLabel: string) => {
  const items = values ?? [];
  if (items.length === 0) {
    return <Text style={styles.procedureEmptyValue}>{emptyLabel}</Text>;
  }

  return items.map((value) => (
    <View key={value} style={styles.cdtChip}>
      <Text style={styles.cdtChipText}>{value}</Text>
    </View>
  ));
};

const ProcedureRow: React.FC<ProcedureRowProps> = ({ step }) => {
  const fee = getProcedureFee(step);
  const cdtEntries = formatCdtCodeEntries(step.codeSuggestions);
  const { teeth, surfaces } = parseToothSurfaces(step.toothSurfaces);

  return (
    <View style={styles.procedureRow} wrap={false}>
      <Text style={styles.procedureTitle}>{step.name}</Text>
      <View style={styles.procedureCdtColumn}>
        {cdtEntries.length > 0 ? (
          cdtEntries.map((entry) => (
            <View key={entry.code} style={styles.cdtChip}>
              <Text style={styles.cdtChipText}>
                {entry.code}
                {entry.count > 1 ? (
                  <Text style={styles.cdtMultiplier}> x{entry.count}</Text>
                ) : (
                  ""
                )}
              </Text>
            </View>
          ))
        ) : (
          <Text style={styles.procedureEmptyValue}>NO CODE</Text>
        )}
      </View>
      <View style={styles.procedureToothColumn}>
        {renderValueChips(teeth, "NO TOOTH")}
      </View>
      <View style={styles.procedureSurfaceColumn}>
        {renderValueChips(surfaces, "NO SURFACE")}
      </View>
      <Text style={styles.procedureFee}>${formatFeeWithoutCents(fee)}</Text>
    </View>
  );
};

interface TimeSeparatorProps {
  timeframeLabel: string;
}

const TimeSeparator: React.FC<TimeSeparatorProps> = ({ timeframeLabel }) => (
  <View style={styles.appointmentTimeframeRow} wrap={false}>
    <View style={styles.timeSeparator}>
      <CalendarIconWhite />
      <Text style={styles.timeSeparatorLabel}>{timeframeLabel}</Text>
      <DownArrowIconWhite />
    </View>
  </View>
);

interface AppointmentProps {
  appointment: TreatmentPlanAppointment;
  appointmentNumber: number;
  isLastAppointment: boolean;
}

const Appointment: React.FC<AppointmentProps> = ({
  appointment,
  appointmentNumber,
  isLastAppointment,
}) => {
  const fee = getAppointmentFee(appointment);
  const duration = getAppointmentDurationMinutes(appointment);
  const optionLabel = getOptionLabel(appointment.conditionalPath);
  const appointmentName =
    appointment.description?.trim() || `Appointment #${appointmentNumber}`;
  const sortedSteps = [...appointment.steps].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  return (
    <View style={styles.appointmentContainer}>
      <View style={styles.appointmentHeader}>
        <Text style={styles.appointmentNumber}>#{appointmentNumber}</Text>
        <View style={styles.appointmentNameWrap}>
          <Text style={styles.appointmentName}>{appointmentName}</Text>
        </View>
        <View style={styles.appointmentMetaGroup}>
          {optionLabel && (
            <Text style={styles.optionBadge}>Opt {optionLabel}</Text>
          )}
          <Text style={styles.appointmentDuration}>{duration} min</Text>
        </View>
        <Text style={styles.appointmentFee}>${formatFeeWithoutCents(fee)}</Text>
      </View>

      {sortedSteps.map((step) => (
        <ProcedureRow key={step.id} step={step} />
      ))}

      {!isLastAppointment && appointment.timeframeLabel && (
        <TimeSeparator timeframeLabel={appointment.timeframeLabel} />
      )}
    </View>
  );
};

interface TreatmentPlanPdfAppointmentsProps {
  appointments: TreatmentPlanAppointment[];
}

export const TreatmentPlanPdfAppointments: React.FC<
  TreatmentPlanPdfAppointmentsProps
> = ({ appointments }) => {
  const sortedAppointments = [...appointments].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  return (
    <>
      <AppointmentsTableHeader />
      {sortedAppointments.map((appointment, index) => (
        <Appointment
          key={appointment.id}
          appointment={appointment}
          appointmentNumber={index + 1}
          isLastAppointment={index === sortedAppointments.length - 1}
        />
      ))}
    </>
  );
};
